import AdyenCheckoutError from '@adyen/adyen-web/dist/types/core/Errors/AdyenCheckoutError'
import React, { FC, useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Redirect, useHistory } from 'react-router-dom'

// hooks
import useURLQueryParams from 'parkdepot-shared/utils/hooks/useURLQueryParams'
import { useGetPaymentData } from './hooks/useGetPaymentData.utils'
import { useOverrideBackButton } from './hooks/useOverrideBackButton.utils'

// style components
import {
    Container,
    ContentContainer,
    Description,
    DescriptionTitle,
    DetailsLine,
    Line,
    RightContainer,
    PriceText,
    SectionTitle,
    Subtitle,
    Text,
    Title,
    VatText
} from './PaymentSuccess.style'
// context
import { CheckoutContext } from '../../context/CheckoutContext/checkout.context'

// routes
import { checkoutRoute } from '../Checkout/route'
import { homeRoute } from '../Home/route.const'

// session storage
import { SESSION_STORAGE_getCheckout } from '../../sessionStorage/checkoutData.sessionStorage'

// components
import FullScreenLoader from '../../components/FullScreenLoader/FullScreenLoader.component'
import PaymentError from '../../components/PaymentError/PaymentError.component'

// types
import { AdyenResultStatus } from '../../types/adyenResultStatus.enum'
import { LotViewName } from '../../types/lot.interface'
import { formatPriceWithCurrency } from '@utils/formatPriceWithCurrency.utils'
import { priceToString } from '@utils/priceToString.utils'
import { calculateVAT } from '@utils/calculateVAT.utils'
import LotInfo from '@views/Checkout/components/LotInfo/LotInfo.component'
import Badge from '@components/Badge/Badge.component'
import { DateTime } from 'luxon'
import { BadgeVariant } from '@components/Badge/types'
import { getDurationStatus } from '@utils/getDurationStatus.utils'
import useLanguage from '@hooks/useLanguage.hook'
import NightParkingTimes from './components/ParkingDetails/NightParkingTimes'
import { DateFormat, DateTimeFormat } from '@constants/dateFormats.consts'

const PaymentSuccess: FC = () => {
    const [urlParams] = useURLQueryParams()

    const { sessionId, redirectResult } = urlParams as {
        booking: string
        sessionId: string
        redirectResult: string
    }

    const { t } = useTranslation()
    const [lng, changeLanguage] = useLanguage()

    const history = useHistory()
    const { checkout, modifyAndSetCheckout } = useContext(CheckoutContext)
    const [loadingCountdown, setLoadingCountdown] = useState(3) // Initial loading dicountdown value in seconds

    const isSubscription = checkout?.lotCheckoutType === LotViewName.SUBSCRIPTION
    const isHourlyBooking = checkout?.lotCheckoutType === LotViewName.HOURLY
    const isNightlyBooking = checkout?.lotCheckoutType === LotViewName.NIGHTLY
    const isDailyBooking = checkout?.lotCheckoutType === LotViewName.DAILY

    const night_parking_times = isNightlyBooking
        ? checkout?.lot?.active_features?.find((feature) => feature.name.toUpperCase() === LotViewName.NIGHTLY)?.config
              .night_parking_times
        : []

    const vatData = calculateVAT(checkout?.price || 0, checkout?.lot?.lot_country_code || 'DE')
    const description = checkout?.lot?.description[lng.split('-')[0]?.toLowerCase() || 'de']
    const [status, setStatus] = useState<{
        tKey: string
        variant: BadgeVariant
    }>({
        tKey: 'checkout.success.active',
        variant: 'positive'
    })

    const onPaymentCompleted = useCallback(
        (resultCode: AdyenResultStatus) => {
            const resultCodeUpperCase = resultCode.toUpperCase() as AdyenResultStatus
            const checkout = SESSION_STORAGE_getCheckout()

            if (checkout && checkout.locale && checkout.locale !== lng) {
                changeLanguage(checkout.locale)
            }

            if (checkout) {
                modifyAndSetCheckout({ ...checkout, paymentStatus: resultCodeUpperCase })
            }
        },
        [modifyAndSetCheckout, changeLanguage, lng]
    )

    const onPaymentError = useCallback((error: AdyenCheckoutError) => {
        // TODO: handle Adyen Checkout error
    }, [])

    useGetPaymentData(sessionId, redirectResult, onPaymentCompleted, onPaymentError)
    useOverrideBackButton()

    const onPaymentErrorBackClick = () => {
        if (checkout) {
            modifyAndSetCheckout({
                ...checkout,
                paymentStatus: undefined
            })
        }
        history.push(homeRoute())
    }

    useEffect(() => {
        if (loadingCountdown === 0) return
        // Start the countdown
        const interval = setInterval(() => {
            setLoadingCountdown((prevCountdown) => prevCountdown - 1)
        }, 1000) // Decrease countdown every second

        // Clean up the interval when the component unmounts or countdown reaches 0
        // eslint-disable-next-line consistent-return
        return () => clearInterval(interval)
    }, [loadingCountdown]) // Empty dependency array to run the effect only once

    useEffect(() => {
        setStatus(getDurationStatus(checkout?.start_datetime || '', checkout?.end_datetime || ''))
    }, [checkout])

    if (!checkout && loadingCountdown > 0) return <FullScreenLoader />

    if (!checkout && loadingCountdown === 0) return <Redirect push to={homeRoute()} />

    if (checkout && (!checkout.paymentStatus || checkout.paymentStatus?.toUpperCase() === AdyenResultStatus.ERROR)) {
        return <PaymentError onBackClick={onPaymentErrorBackClick} paymentStatus={AdyenResultStatus.ERROR} />
    }

    if (checkout && checkout.paymentStatus && checkout.paymentStatus.toUpperCase() === AdyenResultStatus.CANCELLED) {
        return <Redirect push to={checkoutRoute()} />
    }

    if (checkout && checkout.paymentStatus && checkout.paymentStatus.toUpperCase() !== AdyenResultStatus.AUTHORISED) {
        return <PaymentError onBackClick={onPaymentErrorBackClick} paymentStatus={checkout.paymentStatus} />
    }

    return (
        <Container>
            <ContentContainer>
                <Title>{t('checkout.success.title')}</Title>
                <Subtitle>{t('checkout.success.details')}</Subtitle>
                <DetailsLine>
                    <Text>{t('checkout.success.status')}</Text>
                    <Text $right>
                        <Badge variant={status.variant} text={t(status.tKey)} />
                    </Text>
                </DetailsLine>
                <DetailsLine>
                    <Text>{t('checkout.success.vehicle')}</Text>
                    <RightContainer>
                        {checkout?.selectedVehicles?.map((vehicle) => (
                            <Text $right key={vehicle.id}>
                                {vehicle.name ? `${vehicle.name}, ` : ' '}
                                {vehicle.plate}
                            </Text>
                        ))}
                    </RightContainer>
                </DetailsLine>
                <DetailsLine>
                    <Text>{t('checkout.success.email')}</Text>
                    <Text $right>{checkout?.email}</Text>
                </DetailsLine>
                <DetailsLine>
                    <Text>{t('checkout.success.lenght')}</Text>
                    <Text $right>
                        {isSubscription && `${t('subscriptions.duration')}`}
                        {isHourlyBooking &&
                            `${checkout?.duration} ${t(
                                (checkout?.duration || 0) > 1 ? 'common.hours' : 'common.hour'
                            )}`}
                        {isNightlyBooking && (
                            <NightParkingTimes
                                active_features={checkout?.lot?.active_features}
                                sundayIncluded={(night_parking_times?.length ?? 0) > 1}
                            />
                        )}
                        {isDailyBooking && `${t('checkout.allDay')}`}
                    </Text>
                </DetailsLine>
                <DetailsLine>
                    <Text>{t('checkout.success.validFrom')}</Text>
                    <Text $right>
                        {DateTime.fromISO(checkout?.start_datetime as string).toFormat(
                            isHourlyBooking ? DateTimeFormat : DateFormat
                        )}
                    </Text>
                </DetailsLine>
                {(isNightlyBooking || isDailyBooking) && (
                    <DetailsLine>
                        <Text>{t('checkout.success.validTo')}</Text>
                        <Text $right>
                            <span>{DateTime.fromISO(checkout?.end_datetime as string).toFormat(DateFormat)}</span>
                        </Text>
                    </DetailsLine>
                )}
                <DetailsLine>
                    <Text>{t('checkout.success.paymentMethod')}</Text>
                    <Text $right>{checkout?.paymentMethod}</Text>
                </DetailsLine>
                <DetailsLine>
                    <Text>{t('checkout.success.bookingReference')}</Text>
                    <Text $right>{checkout?.bookingNo}</Text>
                </DetailsLine>
                <DetailsLine>
                    <Text $bold>{t('checkout.success.price')}</Text>
                    <RightContainer>
                        <PriceText>{formatPriceWithCurrency().format(priceToString(checkout?.price || 0))}</PriceText>
                        <VatText>
                            {t('checkout.success.included')} {vatData.percentage}% {t('checkout.success.vat')}{' '}
                            {formatPriceWithCurrency().format(priceToString(vatData.value))}
                        </VatText>
                    </RightContainer>
                </DetailsLine>
            </ContentContainer>
            <Line />
            <ContentContainer>
                <SectionTitle>{t('checkout.success.parkingInformation')}</SectionTitle>
                <LotInfo lot={checkout?.lot || null} boldedTitles column />
                <DescriptionTitle>{t('checkout.success.description')}</DescriptionTitle>
                <Description dangerouslySetInnerHTML={{ __html: description || '' }} />
            </ContentContainer>
        </Container>
    )
}

export default PaymentSuccess
